import {
	getActiveSurfacePreset,
	type ActiveSurfaceMode,
	type ActiveSurfacePresetId,
	type ActiveSurfacePresetOverrides,
} from '@Shelly/shelly-design-system';
import { devices_config } from '@devices/index';
import type { DeviceOnOffState } from 'src/helpers/device_control_on_off';
import type { Device } from 'src/types/Device';
import {
	getDashDeviceTemperatureC,
	HT_GLOW_SIZE_OVERRIDES,
	resolveCuryAmbientLightColor,
	resolveDashLightSurfaceColor,
	resolveHtTemperatureGlow,
	resolveRelayPowerGlow,
	scaleGlowSizesByBrightness,
} from './colors';
import { DEFAULT_DASH_LIGHTING_PACK } from './pack-default';
import type {
	DashDeviceActiveSurfaceOverride,
	DashLightingFace,
	DashLightingPack,
	ResolveDashDeviceActiveSurfaceResult,
} from './types';

export type ResolveDashDeviceActiveSurfaceInput = {
	device: Device | undefined | null;
	status?: any;
	settings?: any;
	onOffState: DeviceOnOffState | null | undefined;
	/** When true, apply compact gray `device-state` glow. */
	offline?: boolean;
	/**
	 * Runtime device-state type from `compute-device-state` (0 offline, 4 calibrating,
	 * 7 firmware required, 15 missing component, …). `99` / default = normal.
	 */
	deviceStateType?: number;
	/** External override (gallery / Customize Card / future widget config). */
	override?: DashDeviceActiveSurfaceOverride;
	/**
	 * When true and a usable light color exists, lights / Cury may auto-pick
	 * `light-color` glow. Default true.
	 */
	enableLightColor?: boolean;
	/** Optional light color (hex/oklch/css) when enableLightColor or light-color preset. */
	lightColor?: string | null;
	/** Optional override for tests / gallery (skips devices_config). */
	isPm?: boolean;
};

/** Default “live” state type used by device-runtime (see `defaultStateType`). */
const DEFAULT_DEVICE_STATE_TYPE = 99;

export const isDashPmDevice = (device: Device | undefined | null): boolean => {
	if (!device?.type) return false;
	return !!devices_config?.[device.type]?.isPM;
};

/** True when pack maps this SKU to Cury ambient colour. */
export const isDashCuryDeviceType = (
	type: string | undefined,
	pack: DashLightingPack = DEFAULT_DASH_LIGHTING_PACK,
): boolean => !!type && pack.types[type]?.colorFrom === 'cury-ambient';

/** Flood / smoke / gas status shape — alert refinement without a SKU list. */
export const hasAlertSensorStatus = (status: any): boolean => {
	if (!status) return false;
	if (typeof status.flood === 'boolean' || status['flood:0']) return true;
	if (typeof status.smoke === 'boolean' || status['smoke:0']) return true;
	return !!status.gas_sensor;
};

/**
 * Binary “asserted” state for sensors without a writable on/off control.
 * Returns false when the status shape is unknown (e.g. HT comfort band — handled separately).
 */
export const getDashSensorAssertedState = (
	device: { type?: string; channel?: number } | null | undefined,
	status: any,
): boolean => {
	if (!device?.type || !status) return false;

	const channel = typeof device.channel === 'number' ? device.channel : 0;
	const windowKey = `window:${channel}`;
	if (status[windowKey] && typeof status[windowKey].open === 'boolean') {
		return !!status[windowKey].open;
	}
	if (status['window:0'] && typeof status['window:0'].open === 'boolean') {
		return !!status['window:0'].open;
	}

	const sensorState = status?.sensor?.state;
	if (sensorState === 'open' || sensorState === 'close' || sensorState === 'closed') {
		return sensorState === 'open';
	}

	if (typeof status.motion === 'boolean') return status.motion;
	if (status['motion:0'] && typeof status['motion:0'].motion === 'boolean') {
		return !!status['motion:0'].motion;
	}

	if (typeof status.flood === 'boolean') return status.flood;
	if (status['flood:0'] && typeof status['flood:0'].flood === 'boolean') {
		return !!status['flood:0'].flood;
	}

	if (typeof status.smoke === 'boolean') return status.smoke;
	if (status['smoke:0']) {
		return !!(status['smoke:0'].alarm || status['smoke:0'].smoke);
	}

	const gasAlarm = status?.gas_sensor?.alarm_state;
	if (typeof gasAlarm === 'string' && gasAlarm !== 'none') return true;

	const presenceKeys = Object.keys(status).filter((key) => key.startsWith('presencezone:'));
	if (presenceKeys.length > 0) {
		return presenceKeys.some((key) => {
			const zone = status[key];
			if (!zone) return false;
			if (zone.value) return true;
			return typeof zone.num_objects === 'number' && zone.num_objects > 0;
		});
	}

	return false;
};

const hasNoMainToggleAction = (onOffState: DeviceOnOffState | null | undefined): boolean =>
	!onOffState || !!onOffState.readOnly;

/**
 * Compact saved/blocking-state glow accent from pack refinements.
 * Returns null when the device is in a normal live state.
 */
export const resolveDeviceStateGlowAccent = (
	offline: boolean,
	deviceStateType: number | undefined,
	pack: DashLightingPack = DEFAULT_DASH_LIGHTING_PACK,
): string | null => {
	const accents = pack.refinements.deviceState?.accents;
	if (!accents) return null;

	if (offline || deviceStateType === 0) return accents.offline;
	if (typeof deviceStateType !== 'number' || deviceStateType === DEFAULT_DEVICE_STATE_TYPE) {
		return null;
	}

	switch (deviceStateType) {
		case 7: // firmware required / unsupported
		case 4: // calibrating
		case 15: // missing component / sensor
		case 1: // pending connection
			return accents.warning;
		case 5: // locked
		case 6: // pin locked
		case 9: // unauthorized
		case 14: // disabled
		case 2: // rebooting
		case 3: // updating
		case 8: // loading
		case 10: // updating wifi
		case 11: // associating
			return accents.muted;
		default:
			return accents.muted;
	}
};

/** type → mode → category (relay-pm when isPM) → fallback */
export const lookupLightingFace = (
	device: Device | undefined | null,
	onOffState: DeviceOnOffState | null | undefined,
	pack: DashLightingPack,
	isPm = false,
): DashLightingFace => {
	const type = device?.type;
	if (type && pack.types[type]) return pack.types[type];

	const mode = device?.mode;
	if (mode && pack.modes[mode]) return pack.modes[mode];

	const category = device?.category;
	if (category === 'relay') {
		if (isPm && pack.categories['relay-pm']) return pack.categories['relay-pm'];
		if (pack.categories.relay) return pack.categories.relay;
	} else if (category === 'emeter' || category === 'power_meter' || category === 'pv') {
		// Meter-only → outline/glow withoutToggle face; switchable meter → category face.
		if (hasNoMainToggleAction(onOffState)) {
			return pack.categories['emeter-meter'] ?? pack.fallback.withoutToggle;
		}
		if (pack.categories[category]) return pack.categories[category];
		if (pack.categories.emeter) return pack.categories.emeter;
	} else if (category && pack.categories[category]) {
		return pack.categories[category];
	}

	return hasNoMainToggleAction(onOffState)
		? pack.fallback.withoutToggle
		: pack.fallback.withToggle;
};

const resolveActiveFlag = (
	device: Device | undefined | null,
	status: any,
	onOffState: DeviceOnOffState | null | undefined,
	htActive?: boolean,
): boolean => {
	if (typeof htActive === 'boolean') return htActive;
	if (onOffState && !onOffState.readOnly) return !!onOffState.isOn;
	if (onOffState?.readOnly) return !!onOffState.isOn;
	return getDashSensorAssertedState(device, status);
};

const resolveOverride = (
	override: DashDeviceActiveSurfaceOverride | undefined,
	lightColor?: string | null,
): ResolveDashDeviceActiveSurfaceResult | null => {
	if (!override) return null;

	if (typeof override === 'string') {
		const preset = getActiveSurfacePreset(
			override,
			override === 'light-color' && lightColor
				? { accentColor: lightColor, spotColor: lightColor, edgeColor: lightColor }
				: {},
		);
		return {
			active: false,
			activeSurfaceMode: preset.mode,
			surfaceGlow: preset.surfaceGlow,
			presetId: override,
		};
	}

	if (override.preset) {
		const preset = getActiveSurfacePreset(override.preset, {
			accentColor: override.accentColor,
			backgroundColor: override.backgroundColor ?? lightColor ?? undefined,
			spotColor: override.accentColor,
		});
		return {
			active: false,
			activeSurfaceMode: override.mode ?? preset.mode,
			surfaceGlow: override.surfaceGlow
				? { ...preset.surfaceGlow, ...override.surfaceGlow }
				: preset.surfaceGlow,
			presetId: override.preset,
		};
	}

	if (override.surfaceGlow) {
		return {
			active: false,
			activeSurfaceMode: override.mode ?? 'opaque',
			surfaceGlow: override.surfaceGlow,
			presetId: 'custom',
		};
	}

	return null;
};

const paintPreset = (
	presetId: ActiveSurfacePresetId,
	overrides: ActiveSurfacePresetOverrides = {},
	surfaceMode?: ActiveSurfaceMode,
): ResolveDashDeviceActiveSurfaceResult => {
	const preset = getActiveSurfacePreset(presetId, overrides);
	return {
		active: false,
		activeSurfaceMode: surfaceMode ?? preset.mode,
		surfaceGlow: preset.surfaceGlow,
		presetId,
	};
};

const paintFace = (
	face: DashLightingFace,
	overrides: ActiveSurfacePresetOverrides = {},
): ResolveDashDeviceActiveSurfaceResult =>
	paintPreset(face.preset, overrides, face.surfaceMode);

/**
 * Resolve active face mode + lighting from a swappable pack, then DS presets.
 * Gallery override and `device-state` win first; then type → mode → category → fallback.
 */
export const resolveDashDeviceActiveSurface = (
	input: ResolveDashDeviceActiveSurfaceInput,
	pack: DashLightingPack = DEFAULT_DASH_LIGHTING_PACK,
): ResolveDashDeviceActiveSurfaceResult => {
	const {
		device,
		status,
		settings,
		onOffState,
		offline = false,
		deviceStateType,
		override,
		enableLightColor = true,
		lightColor: lightColorProp,
		isPm: isPmProp,
	} = input;

	const isPm = typeof isPmProp === 'boolean' ? isPmProp : isDashPmDevice(device);

	const overridden = resolveOverride(override, lightColorProp);
	if (overridden) {
		return {
			...overridden,
			active: resolveActiveFlag(device, status, onOffState),
		};
	}

	const stateAccent = resolveDeviceStateGlowAccent(offline, deviceStateType, pack);
	const deviceStatePreset = pack.refinements.deviceState?.preset ?? 'device-state';
	if (stateAccent) {
		return {
			...paintPreset(deviceStatePreset, {
				accentColor: stateAccent,
				spotColor: stateAccent,
				edgeColor: stateAccent,
			}),
			active: false,
		};
	}

	let face = lookupLightingFace(device, onOffState, pack, isPm);

	// Alert refinement: sensor-like faces with flood/smoke/gas status
	if (face.preset === 'sensor' && pack.refinements.alertStatus && hasAlertSensorStatus(status)) {
		face = pack.refinements.alertStatus;
	}

	// Outline info-only chrome — always show soft border, never fill/glow.
	if (face.surfaceMode === 'outline') {
		return {
			...paintFace(face),
			active: true,
			activeSurfaceMode: 'outline',
		};
	}

	// Cury ambient (type face with colorFrom)
	if (face.colorFrom === 'cury-ambient' && enableLightColor) {
		const curyAmbient = resolveCuryAmbientLightColor(device, status, settings);
		if (curyAmbient) {
			const painted = paintFace(face, {
				accentColor: curyAmbient.color,
				spotColor: curyAmbient.color,
				edgeColor: curyAmbient.color,
				hotspotTarget: 'avatar',
			});
			return {
				...painted,
				active: curyAmbient.active,
				surfaceGlow: scaleGlowSizesByBrightness(painted.surfaceGlow, curyAmbient.brightness),
			};
		}
	}

	// Relay blue / PM green (+ power-scaled radius)
	if (face.colorFrom === 'relay-power') {
		const relayGlow = resolveRelayPowerGlow(device, status, isPm);
		const painted = paintFace(face, {
			accentColor: relayGlow.accentColor,
			spotColor: relayGlow.accentColor,
			edgeColor: relayGlow.accentColor,
			hotspotTarget: 'avatar',
		});
		const isOn = resolveActiveFlag(device, status, onOffState);
		return {
			...painted,
			active: isOn,
			surfaceGlow: isOn
				? scaleGlowSizesByBrightness(painted.surfaceGlow, relayGlow.brightness)
				: painted.surfaceGlow,
		};
	}

	// Device light colour (RGB / CCT / prop)
	if (face.colorFrom === 'device') {
		if (!enableLightColor) {
			return {
				...paintPreset('white'),
				active: resolveActiveFlag(device, status, onOffState),
			};
		}

		const glowIntent =
			face.surfaceMode === 'glow' ||
			(face.preset !== 'white' && face.surfaceMode !== 'opaque');

		const fromStatus = resolveDashLightSurfaceColor(device, status, settings, {
			allowLightStatus: true,
		});
		const lightSurface = lightColorProp
			? {
					color: lightColorProp,
					brightness: fromStatus?.brightness ?? 100,
					mode: 'color' as const,
				}
			: fromStatus;

		if (lightSurface && onOffState && !onOffState.readOnly) {
			const painted = paintFace(face, {
				accentColor: lightSurface.color,
				spotColor: lightSurface.color,
				edgeColor: lightSurface.color,
				hotspotTarget: 'avatar',
			});
			return {
				...painted,
				active: !!onOffState.isOn,
				surfaceGlow: scaleGlowSizesByBrightness(painted.surfaceGlow, lightSurface.brightness),
			};
		}

		// No live colour: opaque packs → white; glow packs keep glow preset
		if (!lightColorProp && !fromStatus) {
			const painted = glowIntent
				? paintFace(
						{
							...face,
							preset: face.preset === 'white' ? 'light-color' : face.preset,
						},
						{ hotspotTarget: 'avatar' },
					)
				: paintPreset('white');
			return {
				...painted,
				active: resolveActiveFlag(device, status, onOffState),
			};
		}

		if (lightColorProp) {
			const painted = paintFace(face, {
				accentColor: lightColorProp,
				spotColor: lightColorProp,
				edgeColor: lightColorProp,
			});
			return {
				...painted,
				active: resolveActiveFlag(device, status, onOffState),
			};
		}
	}

	// HT temperature tint on sensor face
	let htGlow: ReturnType<typeof resolveHtTemperatureGlow> | null = null;
	if (
		face.preset === 'sensor' &&
		pack.refinements.htTemperature?.colorFrom === 'ht' &&
		device?.category !== 'emeter' &&
		device?.category !== 'power_meter' &&
		device?.category !== 'pv'
	) {
		const tC = getDashDeviceTemperatureC(status);
		htGlow = tC == null ? null : resolveHtTemperatureGlow(tC);
	}

	const colorOverrides: ActiveSurfacePresetOverrides = htGlow
		? {
				accentColor: htGlow.accentColor,
				spotColor: htGlow.accentColor,
				edgeColor: htGlow.accentColor,
				active: { ...HT_GLOW_SIZE_OVERRIDES.active },
				inactive: { ...HT_GLOW_SIZE_OVERRIDES.inactive },
			}
		: {};

	const painted = paintFace(face, colorOverrides);
	return {
		...painted,
		active: resolveActiveFlag(device, status, onOffState, htGlow?.active),
	};
};
